/**
 * KPI tile — hero figure or a regular stat, with an optional delta.
 * Ported from the `.kpis` row of docs/plans/conversations/09-dashboard-mockup.html.
 *
 * - `hero` sets the figure at 54px with DEFAULT proportional figures: tabular-nums
 *   makes a large standalone number look loose. Column alignment is VizTable's job.
 * - The delta always carries an arrow glyph + the caller's suffix, so colour is
 *   never the only channel.
 * - NO SPARKLINE (removed 2026-10-05). The mockup's tiles carry one because they
 *   stand alone; here « Présence dans le temps » plots the same series on the same
 *   screen, with dated ticks and a real axis — so the sparkline was a strictly
 *   worse duplicate of the chart below it. It also auto-scales min→max, which
 *   turns a two-measured-day period into a confident diagonal.
 */

import { Paper, Typography, type SxProps, type Theme } from '@mui/material';
import { VIZ, fmtDelta } from './tokens';

export interface StatTileProps {
  label: string;
  value: string;
  delta?: { value: number; goodUp?: boolean; suffix?: string };
  foot?: React.ReactNode;
  hero?: boolean;
  sx?: SxProps<Theme>;
}

export default function StatTile({ label, value, delta, foot, hero, sx }: StatTileProps) {
  const d = delta ? fmtDelta(delta.value, delta.goodUp ?? true) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: VIZ.ink2 }}>{label}</Typography>
      <Typography
        component="div"
        sx={{
          fontSize: hero ? 54 : 28,
          lineHeight: 1.05,
          fontWeight: 600,
          letterSpacing: hero ? '-0.02em' : '-0.01em',
          mt: 0.75,
          color: VIZ.ink,
          // deliberately NOT tabular-nums
        }}
      >
        {value}
      </Typography>
      {d && (
        <Typography component="div" sx={{ fontSize: 12, color: d.color, mt: 0.25 }}>
          {d.text}
          {delta?.suffix ? ` ${delta.suffix}` : ''}
        </Typography>
      )}
      {foot && (
        <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 0.75 }}>{foot}</Typography>
      )}
    </Paper>
  );
}
